import { normalizeApiName, shouldSample } from './context.js';
import { countMetric, recordDistribution } from './metrics.js';

const DEFAULT_API_SAMPLE_RATE = 0.1;
const LOGIN_API_NAME = '/v5/ec/order/login';
const EXCLUDED_AUTO_SPAN_PREFIX_LIST = ['/v5/', '/wechat/log'];

/**
 * 判断请求是否应排除接口观测。
 * @param {Object} config - Axios 请求配置。
 * @param {string} apiName - 规范化接口名。
 * @returns {boolean} 是否排除。
 */
function shouldExcludeApiObservation(config, apiName) {
  // 登录接口完整排除，不进入采样、指标或自定义 Span。
  if (apiName === LOGIN_API_NAME) {
    return true;
  }
  // 日志文件上传通过内部 config 标记排除，不解析请求体。
  if (config && config.__stabilityExclude === true) {
    return true;
  }
  return false;
}

/**
 * 判断自动 HTTP Span 是否应创建。
 * @param {string} url - 请求 URL。
 * @returns {boolean} 是否允许自动创建 Span。
 */
export function shouldCreateAutoHttpSpanForRequest(url) {
  const apiName = normalizeApiName(url);
  return !EXCLUDED_AUTO_SPAN_PREFIX_LIST.some((prefix) => {
    return apiName.indexOf(prefix) === 0;
  });
}

/**
 * 创建接口观测对象。
 * @param {Object} config - Axios 请求配置。
 * @returns {Object|null} 接口观测对象。
 */
export function createApiObservation(config = {}) {
  try {
    const apiName = normalizeApiName(config.url || '');
    // 命中完整排除规则时不创建观测对象。
    if (shouldExcludeApiObservation(config, apiName)) {
      return null;
    }
    const method = String(config.method || 'get').toUpperCase();
    return {
      sampled: shouldSample(DEFAULT_API_SAMPLE_RATE),
      apiName,
      method,
      startedAt: Date.now(),
      span: null,
      finished: false
    };
  } catch (error) {
    return null;
  }
}

/**
 * 根据 Axios 原始响应判断业务结果。
 * @param {Object} response - Axios 原始响应。
 * @returns {{result:string,failureType:string}} 结果分类。
 */
export function classifyApiResponse(response) {
  const status = response && response.status;
  const data = response && response.data;
  // HTTP 非 200 统一归为技术失败。
  if (status !== 200) {
    return {
      result: 'technical_failure',
      failureType: 'http_status'
    };
  }
  // 响应体不是对象时归为无效响应。
  if (!data || typeof data !== 'object') {
    return {
      result: 'technical_failure',
      failureType: 'invalid_response'
    };
  }
  // 业务码不是数值时归为无效响应。
  if (typeof data.code !== 'number') {
    return {
      result: 'technical_failure',
      failureType: 'invalid_response'
    };
  }
  // 业务码为 0 表示成功。
  if (data.code === 0) {
    return {
      result: 'success',
      failureType: 'none'
    };
  }
  return {
    result: 'business_failure',
    failureType: 'none'
  };
}

/**
 * 根据 Axios 错误判断技术结果。
 * @param {*} error - Axios 错误对象。
 * @param {Object} AxiosObject - Axios 模块。
 * @returns {{result:string,failureType:string}} 结果分类。
 */
export function classifyApiError(error, AxiosObject) {
  // 主动取消请求不进入失败率分子。
  if (AxiosObject && AxiosObject.isCancel && AxiosObject.isCancel(error)) {
    return {
      result: 'cancelled',
      failureType: 'cancelled'
    };
  }
  const code = String((error && error.code) || '');
  const message = String((error && error.message) || '');
  // 超时错误归为 timeout。
  if (code === 'ECONNABORTED' || message.indexOf('timeout') > -1) {
    return {
      result: 'technical_failure',
      failureType: 'timeout'
    };
  }
  // 有 HTTP 响应但进入 rejection 时归为 http_status。
  if (error && error.response && error.response.status) {
    return {
      result: 'technical_failure',
      failureType: 'http_status'
    };
  }
  return {
    result: 'technical_failure',
    failureType: 'network'
  };
}

/**
 * 完成接口观测。
 * @param {Object|null} observation - 接口观测对象。
 * @param {Object} result - 结果分类。
 * @param {string} [result.result] - success、technical_failure、business_failure 或 cancelled。
 * @param {string} [result.failureType] - 技术失败细分。
 * @returns {void}
 */
export function finishApiObservation(observation, result = {}) {
  try {
    // 无观测对象、已完成或未采样时不写入指标。
    if (!observation || observation.finished || !observation.sampled) {
      return;
    }
    observation.finished = true;
    const finalResult = result.result || 'success';
    // 主动取消不进入失败率分子或分母。
    if (finalResult === 'cancelled') {
      return;
    }
    const attributes = {
      api_name: observation.apiName,
      method: observation.method,
      result: finalResult,
      failure_type: result.failureType || 'none'
    };
    countMetric('h5.api.request', attributes);
    recordDistribution(
      'h5.api.duration',
      Date.now() - observation.startedAt,
      'millisecond',
      attributes
    );
  } catch (error) {}
}
